import { c as _c } from "react/compiler-runtime";
import { useEffect, useState } from "react";
import { formatUnits } from "viem";
import { useConnection, useReadContracts } from "wagmi";
import { type RequiredNftPoolChainType, fallbackChainId, usdContract, wethContract as wethContractAddress, wethUsdPair } from "../constants";
import { ChainId, type NftPoolConfig, type PoolId, multicallAddress } from "../constants";
import { getPoolSettings } from "../util";
import { tokenAbi } from "../constants/abi";

/**
 * Custom hook to fetch and calculate token prices for a given pool.
 *
 * @param poolId - The ID of the pool.
 * @returns An object containing the bone price, reward price, dn404 price, and a refresh function.
 */
export function useTokenPrice(poolId) {
  const $ = _c(79);
  const [dn404Price, setDn404Price] = useState(0);
  const [bonePrice, setBonePrice] = useState(0);
  const [rewardPrice, setRewardPrice] = useState(0);
  const {
    chainId
  } = useConnection();
  const t0 = chainId as RequiredNftPoolChainType ?? fallbackChainId as RequiredNftPoolChainType;
  let t1;
  if ($[0] !== poolId || $[1] !== t0) {
    t1 = getPoolSettings(t0, poolId);
    $[0] = poolId;
    $[1] = t0;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const currPool = t1;
  chainId ?? fallbackChainId;
  let t2;
  if ($[3] !== currPool.rewardPair) {
    t2 = {
      address: currPool.rewardPair,
      abi: tokenAbi
    };
    $[3] = currPool.rewardPair;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const rewardPair = t2 as const;
  let t3;
  if ($[5] !== currPool._404Pair) {
    t3 = {
      address: currPool._404Pair,
      abi: tokenAbi
    };
    $[5] = currPool._404Pair;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const dn404Pair = t3 as const;
  const t4 = wethUsdPair[chainId as ChainId ?? fallbackChainId];
  let t5;
  if ($[7] !== t4) {
    t5 = {
      address: t4,
      abi: tokenAbi
    };
    $[7] = t4;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  const usdtPair = t5 as const;
  let t6;
  if ($[9] !== currPool._404Token) {
    t6 = {
      address: currPool._404Token,
      abi: tokenAbi
    };
    $[9] = currPool._404Token;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  const dn404Contract = t6 as const;
  let t7;
  if ($[11] !== currPool.rewardAddress) {
    t7 = {
      address: currPool.rewardAddress,
      abi: tokenAbi
    };
    $[11] = currPool.rewardAddress;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  const rewardContract = t7 as const;
  const t8 = usdContract[chainId as ChainId ?? fallbackChainId];
  let t9;
  if ($[13] !== t8) {
    t9 = {
      address: t8,
      abi: tokenAbi
    };
    $[13] = t8;
    $[14] = t9;
  } else {
    t9 = $[14];
  }
  const usdtContract = t9 as const;
  const t10 = wethContractAddress[chainId as ChainId ?? fallbackChainId];
  let t11;
  if ($[15] !== t10) {
    t11 = {
      address: t10,
      abi: tokenAbi
    };
    $[15] = t10;
    $[16] = t11;
  } else {
    t11 = $[16];
  }
  const wethContract = t11 as const;
  let t12;
  if ($[17] !== usdtPair.address) {
    t12 = [usdtPair.address];
    $[17] = usdtPair.address;
    $[18] = t12;
  } else {
    t12 = $[18];
  }
  let t13;
  if ($[19] !== t12 || $[20] !== usdtContract) {
    t13 = {
      ...usdtContract,
      functionName: "balanceOf",
      args: t12
    };
    $[19] = t12;
    $[20] = usdtContract;
    $[21] = t13;
  } else {
    t13 = $[21];
  }
  let t14;
  if ($[22] !== usdtPair.address) {
    t14 = [usdtPair.address];
    $[22] = usdtPair.address;
    $[23] = t14;
  } else {
    t14 = $[23];
  }
  let t15;
  if ($[24] !== t14 || $[25] !== wethContract) {
    t15 = {
      ...wethContract,
      functionName: "balanceOf",
      args: t14
    };
    $[24] = t14;
    $[25] = wethContract;
    $[26] = t15;
  } else {
    t15 = $[26];
  }
  let t16;
  if ($[27] !== rewardPair.address) {
    t16 = [rewardPair.address];
    $[27] = rewardPair.address;
    $[28] = t16;
  } else {
    t16 = $[28];
  }
  let t17;
  if ($[29] !== rewardContract || $[30] !== t16) {
    t17 = {
      ...rewardContract,
      functionName: "balanceOf",
      args: t16
    };
    $[29] = rewardContract;
    $[30] = t16;
    $[31] = t17;
  } else {
    t17 = $[31];
  }
  let t18;
  if ($[32] !== rewardPair.address) {
    t18 = [rewardPair.address];
    $[32] = rewardPair.address;
    $[33] = t18;
  } else {
    t18 = $[33];
  }
  let t19;
  if ($[34] !== t18 || $[35] !== wethContract) {
    t19 = {
      ...wethContract,
      functionName: "balanceOf",
      args: t18
    };
    $[34] = t18;
    $[35] = wethContract;
    $[36] = t19;
  } else {
    t19 = $[36];
  }
  let t20;
  if ($[37] !== t13 || $[38] !== t15 || $[39] !== t17 || $[40] !== t19) {
    t20 = [t13, t15, t17, t19];
    $[37] = t13;
    $[38] = t15;
    $[39] = t17;
    $[40] = t19;
    $[41] = t20;
  } else {
    t20 = $[41];
  }
  const t21 = multicallAddress[chainId as ChainId ?? ChainId.SHIBARIUM];
  let t22;
  if ($[42] !== t20 || $[43] !== t21) {
    t22 = {
      allowFailure: false,
      contracts: t20,
      multicallAddress: t21
    };
    $[42] = t20;
    $[43] = t21;
    $[44] = t22;
  } else {
    t22 = $[44];
  }
  const {
    data: priceData,
    refetch
  } = useReadContracts(t22);
  let t23;
  if ($[45] !== dn404Pair.address) {
    t23 = [dn404Pair.address];
    $[45] = dn404Pair.address;
    $[46] = t23;
  } else {
    t23 = $[46];
  }
  let t24;
  if ($[47] !== dn404Contract || $[48] !== t23) {
    t24 = {
      ...dn404Contract,
      functionName: "balanceOf",
      args: t23
    };
    $[47] = dn404Contract;
    $[48] = t23;
    $[49] = t24;
  } else {
    t24 = $[49];
  }
  let t25;
  if ($[50] !== dn404Pair.address) {
    t25 = [dn404Pair.address];
    $[50] = dn404Pair.address;
    $[51] = t25;
  } else {
    t25 = $[51];
  }
  let t26;
  if ($[52] !== t25 || $[53] !== wethContract) {
    t26 = {
      ...wethContract,
      functionName: "balanceOf",
      args: t25
    };
    $[52] = t25;
    $[53] = wethContract;
    $[54] = t26;
  } else {
    t26 = $[54];
  }
  let t27;
  if ($[55] !== t24 || $[56] !== t26) {
    t27 = [t24, t26];
    $[55] = t24;
    $[56] = t26;
    $[57] = t27;
  } else {
    t27 = $[57];
  }
  const t28 = multicallAddress[chainId as ChainId ?? ChainId.SHIBARIUM];
  let t29;
  if ($[58] !== t27 || $[59] !== t28) {
    t29 = {
      allowFailure: false,
      contracts: t27,
      multicallAddress: t28
    };
    $[58] = t27;
    $[59] = t28;
    $[60] = t29;
  } else {
    t29 = $[60];
  }
  const {
    data: dn404Data,
    refetch: refetch404
  } = useReadContracts(t29);
  let t30;
  if ($[61] !== refetch || $[62] !== refetch404) {
    t30 = () => {
      refetch();
      refetch404();
    };
    $[61] = refetch;
    $[62] = refetch404;
    $[63] = t30;
  } else {
    t30 = $[63];
  }
  const refreshPrice = t30;
  let t31;
  if ($[64] !== currPool._404TokenDecimals || $[65] !== currPool.rewardDecimals || $[66] !== dn404Data || $[67] !== priceData) {
    t31 = () => {
      if (priceData) {
        const usdtPerWeth = Number(formatUnits((priceData as any)?.[0], 6)) / Number(formatUnits((priceData as any)?.[1], 18));
        const wethPerReward = Number(formatUnits((priceData as any)?.[3], 18)) / Number(formatUnits((priceData as any)?.[2], currPool.rewardDecimals));
        const usdtPerReward = usdtPerWeth * wethPerReward;
        setBonePrice(usdtPerWeth);
        setRewardPrice(usdtPerReward);
        if (dn404Data) {
          const wethPerDn404 = Number(formatUnits((dn404Data as any)?.[1], 18)) / Number(formatUnits((dn404Data as any)?.[0], currPool._404TokenDecimals));
          setDn404Price(usdtPerWeth * wethPerDn404);
        }
      }
    };
    $[64] = currPool._404TokenDecimals;
    $[65] = currPool.rewardDecimals;
    $[66] = dn404Data;
    $[67] = priceData;
    $[68] = t31;
  } else {
    t31 = $[68];
  }
  let t32;
  if ($[69] !== currPool || $[70] !== dn404Data || $[71] !== poolId || $[72] !== priceData) {
    t32 = [priceData, dn404Data, poolId, currPool];
    $[69] = currPool;
    $[70] = dn404Data;
    $[71] = poolId;
    $[72] = priceData;
    $[73] = t32;
  } else {
    t32 = $[73];
  }
  useEffect(t31, t32);
  let t33;
  if ($[74] !== bonePrice || $[75] !== dn404Price || $[76] !== refreshPrice || $[77] !== rewardPrice) {
    t33 = {
      bonePrice,
      rewardPrice,
      dn404Price,
      refreshPrice
    };
    $[74] = bonePrice;
    $[75] = dn404Price;
    $[76] = refreshPrice;
    $[77] = rewardPrice;
    $[78] = t33;
  } else {
    t33 = $[78];
  }
  return t33;
}